import React, { useEffect, useState, useMemo } from "react";
import {
  getAccessoryImages,
} from "../../services/image_services";
import colors from "../../styles/colors";
import ProductMain from "../../components/ProductMain";
import ContactUsSection from "../../components/HomeSectionContainers/ContactUsSectoin";
import { useTranslation } from "react-i18next";
import AccessoryDesc from "../../components/AccessoryDesc";
import SEO from "../../utils/SEO";

interface Accessory {
  name: string;
  title: string;
  description: string;
  imageUrl: string | null;
}

const Accessories: React.FC = () => {
  const { t } = useTranslation();
  const [accessoriesWithImages, setAccessoriesWithImages] = useState<
    Accessory[]
  >([]);

  // A fordított szövegeket mindig frissíteni kell, ha változik a nyelv
  const accessories = useMemo(
    () => [
      {
        name: "furniture1.webp",
        title: t("accessories.items.furniture.title"),
        description: t("accessories.items.furniture.description"),
        imageUrl: null,
      },
      {
        name: "boiserie3.webp",
        title: t("accessories.items.boiseries.title"),
        description: t("accessories.items.boiseries.description"),
        imageUrl: null,
      },
      {
        name: "blinds1.webp",
        title: t("accessories.items.blinds.title"),
        description: t("accessories.items.blinds.description"),
        imageUrl: null,
      },
      {
        name: "shutter1.webp",
        title: t("accessories.items.shutters.title"),
        description: t("accessories.items.shutters.description"),
        imageUrl: null,
      },
      {
        name: "insect_screen1.webp",
        title: t("accessories.items.insect_screen.title"),
        description: t("accessories.items.insect_screen.description"),
        imageUrl: null,
      },
      {
        name: "stairs2.webp",
        title: t("accessories.items.stairs.title"),
        description: t("accessories.items.stairs.description"),
        imageUrl: null,
      },
      {
        name: "window_sills1.webp",
        title: t("accessories.items.window_sills.title"),
        description: t("accessories.items.window_sills.description"),
        imageUrl: null,
      },
      {
        name: "kilincs2.webp",
        title: t("accessories.items.handles.title"),
        description: t("accessories.items.handles.description"),
        imageUrl: null,
      },
    ],
    [t] // Ha a `t` függvény változik (azaz a nyelv változik), újrageneráljuk a tömböt
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = "auto";
    };
  }, []);

  useEffect(() => {
    const fetchAccessoryImages = async () => {
      try {
        const accessoryImages = await getAccessoryImages(); // Lokális képlekérdezés

        const updatedAccessories = accessories.map((accessory) => ({
          ...accessory,
          imageUrl: accessoryImages[accessory.name] || null, // Ha nincs kép, akkor null marad
        }));

        setAccessoriesWithImages(updatedAccessories);
      } catch (error) {
        console.error(
          "Hiba történt a kiegészítők képeinek betöltésekor:",
          error
        );
      }
    };

    fetchAccessoryImages();
  }, [accessories]);

  return (
    <div
      className="flex flex-col items-center font-serif justify-center min-h-screen overflow-hidden"
      style={{ backgroundColor: colors.pastelCream }}
    >
      {/* SEO meta tags */}
      <SEO
        title={t("seo_meta.accessories.metaTitle")}
        description={t("seo_meta.accessories.metaDescription")}
      />

      <ProductMain
        image="/assets/images/products/accessories/accessories_main.webp"
        title={t("accessories.title")}
        description={t("accessories.subTitle")}
      />

      <div className="flex flex-col gap-14">
        {accessoriesWithImages.map(
          (accessory, index) =>
            accessory.imageUrl && (
              <AccessoryDesc
                key={accessory.name}
                image={accessory.imageUrl}
                title={accessory.title}
                text={accessory.description}
                imageOnRight={index % 2 === 0}
              />
            )
        )}
      </div>

      {/* Horizontal Divider */}
      <div
        className="w-full max-h-[20vh] pt-24 pb-24 flex justify-center"
        style={{ backgroundColor: colors.pastelCream }}
      >
        <hr
          className="w-[23%] sm:w-[15%] md:w-[15%] lg:w-[10%] h-0.5 border-t-0 opacity-100 dark:opacity-80"
          style={{ backgroundColor: colors.darkBrown }}
        />
      </div>

      <ContactUsSection />
    </div>
  );
};

export default Accessories;
